import type { KeyBindingsConfig } from '../../shared/CharacterTypes';

// Default Key Bindings (MapleStory-style + WASD/Arrows)
// Centralized configuration for inputs
export const DEFAULT_KEY_BINDINGS: KeyBindingsConfig = {
  bindings: [
    // Movement
    { action: 'move_left', key: 'A', category: 'movement' },
    { action: 'move_left', key: 'LEFT', category: 'movement' },
    { action: 'move_right', key: 'D', category: 'movement' },
    { action: 'move_right', key: 'RIGHT', category: 'movement' },
    { action: 'move_up', key: 'W', category: 'movement' },
    { action: 'move_up', key: 'UP', category: 'movement' },
    { action: 'move_down', key: 'S', category: 'movement' },
    { action: 'move_down', key: 'DOWN', category: 'movement' },
    { action: 'jump', key: 'SPACE', category: 'movement' },
    { action: 'jump', key: 'W', category: 'movement' }, // Alternative jump
    { action: 'jump', key: 'UP', category: 'movement' }, // Alternative jump
    { action: 'autowalk', key: 'NUMLOCK', category: 'movement' },

    // Combat. basic_attack is the action-melee-floor auto attack
    // (CombatManager.processAttack('basic') — nearest-in-melee-range).
    // 2026-06-11: key 1 moved OFF basic_attack and onto hotbar slot 1
    // (ability_slot_1 below) so the bar's first slot honors drag-drop like
    // slots 2-10. basic_attack stays keyboard-reachable on F (rebindable).
    { action: 'basic_attack', key: 'F', category: 'combat' },
    { action: 'secondary_attack', key: 'X', category: 'combat' },
    { action: 'free_aim', key: 'ALT', category: 'combat' },

    // Bar slots (modular bars plan, phase 3): bar1_slotN fires slot N on bar 1's
    // current page. Keys 1–9, 0 and the numpad reach slots 1–10; other bars and
    // the page flips (bar<B>_page_next/prev/<N>) start unbound (D5). Saves
    // from before v3.0 named these ability_slot_1 and ability_1..9 (slot N+1);
    // KeyBindingManager renames them on load.
    { action: 'bar1_slot1', key: '1', category: 'combat' },
    { action: 'bar1_slot1', key: 'NUMPAD_ONE', category: 'combat' },
    { action: 'bar1_slot2', key: '2', category: 'combat' },
    { action: 'bar1_slot2', key: 'NUMPAD_TWO', category: 'combat' },
    { action: 'bar1_slot3', key: '3', category: 'combat' },
    { action: 'bar1_slot3', key: 'NUMPAD_THREE', category: 'combat' },
    { action: 'bar1_slot4', key: '4', category: 'combat' },
    { action: 'bar1_slot4', key: 'NUMPAD_FOUR', category: 'combat' },
    { action: 'bar1_slot5', key: '5', category: 'combat' },
    { action: 'bar1_slot5', key: 'NUMPAD_FIVE', category: 'combat' },
    { action: 'bar1_slot6', key: '6', category: 'combat' },
    { action: 'bar1_slot6', key: 'NUMPAD_SIX', category: 'combat' },
    { action: 'bar1_slot7', key: '7', category: 'combat' },
    { action: 'bar1_slot7', key: 'NUMPAD_SEVEN', category: 'combat' },
    { action: 'bar1_slot8', key: '8', category: 'combat' },
    { action: 'bar1_slot8', key: 'NUMPAD_EIGHT', category: 'combat' },
    { action: 'bar1_slot9', key: '9', category: 'combat' },
    { action: 'bar1_slot9', key: 'NUMPAD_NINE', category: 'combat' },
    { action: 'bar1_slot10', key: '0', category: 'combat' },
    { action: 'bar1_slot10', key: 'NUMPAD_ZERO', category: 'combat' },

    // UI
    { action: 'inventory', key: 'I', category: 'ui' },
    { action: 'inventory', key: 'B', category: 'ui' }, // Bag
    { action: 'character_sheet', key: 'C', category: 'ui' },
    { action: 'talents', key: 'N', category: 'ui' },
    { action: 'skills', key: 'K', category: 'ui' },
    { action: 'quest_log', key: 'L', category: 'ui' },
    // World Map on M (lane AN, 2026-10-08, DJ). M used to open the Map Picker,
    // a debug warp list that now lives on the dev surface only; the brief
    // O default (lane AK, v2.5) retired with the move.
    { action: 'world_map', key: 'M', category: 'ui' },
    { action: 'party', key: 'P', category: 'ui' },
    // Leaderboard (lane BM, 2026-10-10, plan 23 row 40): O is free since the World Map moved to M (v2.6).
    { action: 'leaderboard', key: 'O', category: 'ui' },
    // QuickMenu rebuild (2026-06-12): every player window is bindable — the
    // old MicroMenu tooltips advertised these keys without binding them.
    { action: 'trade', key: 'T', category: 'ui' },
    { action: 'bank', key: 'V', category: 'ui' },
    { action: 'crafting', key: 'J', category: 'ui' },

    // Misc
    { action: 'interact', key: 'E', category: 'misc' },
    { action: 'sit', key: 'S', modifier: 'ctrl', category: 'misc' },

    // Targeting
    { action: 'target_next', key: 'TAB', category: 'targeting' },
    { action: 'target_prev', key: 'TAB', modifier: 'shift', category: 'targeting' },
    // Backtick unbound (2026-06-09) — it crashed the client; TAB cycling already
    // covers targeting. Backtick is a no-op now across all input paths.
    { action: 'clear_target', key: 'ESCAPE', category: 'targeting' },
    { action: 'toggle_autoattack', key: 'A', category: 'targeting' },
    { action: 'loot', key: 'E', category: 'misc' },

    // GM Event — fishing cast/reel (context-sensitive: same key handles both
    // depending on the current fishing state). Uses F by default; rebindable.
    { action: 'fishing_cast', key: 'F', category: 'event' },
    { action: 'fishing_reel', key: 'F', category: 'event' }
  ],
  version: '3.0' // Character C; Talents N; Quest Log L; free_aim Alt; World Map M; Leaderboard O; autowalk NumLock; bar slots bar1_slotN, numpad 7–0.
};
